import {
  Box,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  StackDivider,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import {
  AlertTriangle,
  CheckCircle,
  Flag,
  Info,
  Key,
  MinusCircle,
  Search,
  XCircle,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type Ref } from "react";
import { MFAccessReviewAccountEntitlement } from "state/members/accessReviews";
import {
  Button,
  Switch as DsSwitch,
  Textarea as DsTextarea,
} from "components/ds";
import {
  adjustmentConversionCount,
  adjustmentScopeNotice,
  decisionSubject,
  entitlementChipLabel,
  entitlementRemovalLine,
  fullCoverageNotice,
  isEntitlementAdjustmentOutcome,
  isLockingOutcome,
  decisionRemovalTiming,
  decisionRemovesAccessNow,
  offersEntitlementFlagging,
  decisionCancelsPendingRemoval,
  pendingRemovalNotice,
  removalConsequenceLine,
  submittedOutcomeFor,
  flagsForGroups,
  pickerUnits,
  toggleFilterValue,
  type DecisionRemoval,
  type GrantingGroup,
} from "../reviewLogic";

// Per-outcome treatment for the confirmation dialog — tinted header disc, solid
// Confirm-button tone, and the consequence sentence. Keyed by standard_mapping;
// falls back to a neutral treatment for any custom mapping.
type ConfirmTone = "success" | "error" | "neutral" | "warning";

const TONE_ICON_TINT: Record<ConfirmTone, { bg: string; color: string }> = {
  success: { bg: "success.50", color: "success.600" },
  error: { bg: "error.50", color: "error.600" },
  neutral: { bg: "bg.section", color: "text.body.secondary" },
  warning: { bg: "warning.50", color: "warning.600" },
};

// `removal` is consulted ONLY by the removal outcomes below: what the reviewer
// is told happens to the access has to come from the cycle's configured
// actions, not from the outcome being called "not required" (PLO-5804).
const outcomeMeta = (
  mapping: string | null,
  removal: DecisionRemoval,
): { tone: ConfirmTone; icon: typeof CheckCircle; line: string } => {
  switch (mapping) {
    case "required":
    case "keep":
    case "compliant":
    case "appropriate":
      return {
        tone: "success",
        icon: CheckCircle,
        line: "Their access will be kept.",
      };
    case "not_required":
    case "non_compliant":
    case "not_appropriate":
    case "remove":
      return {
        tone: "error",
        icon: XCircle,
        line: removalConsequenceLine(removal),
      };
    case "adjust_entitlements":
    case "modify":
      return {
        tone: "warning",
        icon: AlertTriangle,
        line: "The flagged entitlements will be adjusted.",
      };
    case "removed":
      return {
        tone: "neutral",
        icon: MinusCircle,
        line: "This access will be recorded as already removed.",
      };
    case "out_of_scope":
    default:
      return {
        tone: "neutral",
        icon: MinusCircle,
        line: "This access will be excluded from the review.",
      };
  }
};

// Note/reason field — matches the design's stacked label + full-width textarea,
// with an optional red asterisk when the outcome makes it mandatory.
const NoteField = ({
  label,
  required,
  value,
  onChange,
  placeholder,
  inputRef,
}: {
  label: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  inputRef?: Ref<HTMLTextAreaElement>;
}) => (
  <FormControl isRequired={required}>
    <FormLabel
      fontSize="smd"
      fontWeight={500}
      color="text.body.secondary"
      mb="1xs"
      requiredIndicator={
        <Text as="span" color="error.600" ml="2px">
          *
        </Text>
      }
    >
      {label}
    </FormLabel>
    <DsTextarea
      ref={inputRef}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      borderRadius="component.2xl"
      borderColor="border.default"
      minH="88px"
    />
  </FormControl>
);

// The picker's tick box; stops the click so the row's own onClick doesn't
// toggle a second time.
const PickerCheckbox = ({
  checked,
  onToggle,
}: {
  checked: boolean;
  onToggle: () => void;
}) => (
  <Box onClick={(e) => e.stopPropagation()} display="flex" alignItems="center">
    <Checkbox isChecked={checked} onChange={onToggle} />
  </Box>
);

// Selected/hover tint shared by a standalone PickerRow and a TogetherBlock.
const pickerTint = (checked: boolean) => ({
  bg: checked ? "bg.warning" : undefined,
  _hover: { bg: checked ? "warning.100" : "bg.hover" },
  transition: "background 0.15s ease",
});

// One tickable entitlement in the adjustment picker. `nested` rows sit inside a
// TogetherBlock, which owns the hover and selected tint for the whole block.
type PickerRowProps = {
  checked: boolean;
  onToggle: () => void;
  title: string;
  subtitle?: string | null;
  nested?: boolean;
};

const PickerRow = ({
  checked,
  onToggle,
  title,
  subtitle,
  nested = false,
}: PickerRowProps) => (
  <HStack
    py={nested ? "1xs" : "sm"}
    pl={nested ? "2xl" : "md"}
    pr="md"
    spacing="md"
    align="center"
    {...(nested ? {} : pickerTint(checked))}
    cursor="pointer"
    onClick={onToggle}
  >
    <PickerCheckbox checked={checked} onToggle={onToggle} />
    <Icon
      as={Key}
      boxSize={4}
      color={checked ? "warning.500" : "text.body.tertiary"}
    />
    <VStack align="start" spacing={0} flex={1}>
      <Text fontSize="smd" fontWeight={500} color="text.body.primary">
        {title}
      </Text>
      {subtitle && (
        <Text fontSize="xs" color="text.body.tertiary">
          {subtitle}
        </Text>
      )}
    </VStack>
  </HStack>
);

// Group-held entitlements (PLO-6260): they come and go with the group that
// grants them, so ticking any one of them ticks the whole block.
const TogetherBlock = ({
  checked,
  onToggle,
  groupName,
  entitlementNames,
}: {
  checked: boolean;
  onToggle: () => void;
  groupName: string;
  entitlementNames: string[];
}) => (
  <Box pb="1xs" {...pickerTint(checked)}>
    <HStack
      px="md"
      pt="sm"
      pb="2xs"
      spacing="md"
      cursor="pointer"
      onClick={onToggle}
    >
      <PickerCheckbox checked={checked} onToggle={onToggle} />
      <HStack spacing="2xs">
        <Text fontSize="xs" fontWeight={500} color="text.body.tertiary">
          Must be removed together
        </Text>
        <Tooltip
          label={`Granted through ${groupName}, so these are removed together by removing the person from it.`}
          placement="top"
          hasArrow
        >
          <Box
            as="span"
            display="inline-flex"
            onClick={(e) => e.stopPropagation()}
          >
            <Icon
              as={Info}
              boxSize={3.5}
              color="text.body.tertiary"
              aria-label={`Granted through ${groupName}`}
            />
          </Box>
        </Tooltip>
      </HStack>
    </HStack>
    {entitlementNames.map((name, i) => (
      <PickerRow
        key={`${name}-${i}`}
        checked={checked}
        onToggle={onToggle}
        title={name}
        nested
      />
    ))}
  </Box>
);

const ReviewModal = ({
  isOpen,
  onClose,
  reviewAction,
  selectedCount,
  selectedAccountName,
  outOfScopeReason,
  setOutOfScopeReason,
  note,
  setNote,
  onSubmit,
  isSubmitting,
  outcomeOptions,
  isResourceOwner = false,
  markAsRemoved,
  setMarkAsRemoved,
  decisionOnly = false,
  // Entitlement flagging props
  entitlements = [],
  accountEntitlementIds = [],
  grantingGroups = [],
  adjustSeparately = false,
  flaggedSnapshotIds = [],
  setFlaggedSnapshotIds,
  entitlementOnly = false,
  pendingRemovalCount = 0,
}: {
  isOpen: boolean;
  onClose: () => void;
  reviewAction: string | null;
  selectedCount: number;
  selectedAccountName?: string;
  outOfScopeReason: string;
  setOutOfScopeReason: (reason: string) => void;
  note: string;
  setNote: (note: string) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  // `actions` is what the cycle runs the moment this outcome is recorded. The
  // warning, consent gate and consequence sentence below all read it, so a
  // caller that drops it makes this dialog fail closed (PLO-5804).
  outcomeOptions: Array<{
    name: string;
    standard_mapping: string;
    requires_note?: boolean;
    requires_notes?: boolean;
    action_type?: string;
    actions?: Array<{ type?: string | null } | null> | null;
  }>;
  isResourceOwner?: boolean;
  // When the cycle only records decisions (never deprovisions), suppress the
  // deprovisioning warning + consent gate (PLO-5083).
  decisionOnly?: boolean;
  markAsRemoved?: boolean;
  setMarkAsRemoved?: (value: boolean) => void;
  // Entitlement flagging props
  entitlements?: MFAccessReviewAccountEntitlement[];
  // One entry per selected account, holding that account's own snapshot
  // entitlement ids. `entitlements` above is the merged list the dialog
  // renders; coverage has to be judged per account (see reviewLogic).
  accountEntitlementIds?: string[][];
  // Group-held entitlements are ticked together, a block per granting group —
  // see `grantingGroups`. `adjustSeparately` means one flag set cannot speak
  // for every selected account, so nothing groups.
  grantingGroups?: GrantingGroup[];
  adjustSeparately?: boolean;
  flaggedSnapshotIds?: string[];
  setFlaggedSnapshotIds?: (ids: string[]) => void;
  // Entitlement-only cycle (PLO-5731): the decision covers ONE entitlement per
  // selected row, not the account. The flag-entitlements picker is dropped (the
  // row's only entitlement is already what is being decided), the fully-flagged
  // → removal rewrite cannot apply, and a removal says which entitlement goes
  // and that the rest of the account is untouched.
  entitlementOnly?: boolean;
  // Selected rows whose removal has not happened yet. A decision that keeps
  // the access calls those removals off, and the dialog says so (PLO-5749).
  pendingRemovalCount?: number;
}) => {
  const noteFieldRef = useRef<HTMLTextAreaElement>(null);

  // Find the matching outcome option
  const selectedOption = outcomeOptions.find(
    (opt) => opt.standard_mapping === reviewAction,
  );

  // Determine if a note is required for this outcome
  // Support both requires_note and requires_notes for backward compatibility
  const requiresNote =
    selectedOption?.requires_note ||
    selectedOption?.requires_notes ||
    reviewAction === "out_of_scope";

  const isBulk = selectedCount > 1;

  // What the dialog is deciding about, and the bare noun for it. In an
  // entitlement-only cycle the selection is entitlements, one per row, so
  // anything counting "accounts" would name the wrong thing (PLO-5731).
  const subject = decisionSubject(
    selectedCount,
    selectedAccountName,
    entitlementOnly,
  );
  const noun = entitlementOnly ? "entitlement" : "account";

  // Sentence-case title: "Mark access as not required" / bulk "Mark N accounts
  // as not required". Falls back to a generic title for an unknown mapping. A
  // bulk title IS the subject, so it reads it off the same helper.
  const title = (() => {
    if (!selectedOption) return "Review access";
    const label = selectedOption.name.toLowerCase();
    return isBulk ? `Mark ${subject} as ${label}` : `Mark access as ${label}`;
  })();

  // Check if the current outcome triggers entitlement adjustment
  const triggersEntitlementAdjustment =
    isEntitlementAdjustmentOutcome(selectedOption);

  // An adjustment only touches the entitlements the reviewer flags, so say so
  // before they confirm one thinking it removes the account (PLO-5460). On
  // group-held access the reviewer picks what is removed together, so it does
  // not apply.
  const scopeNotice =
    triggersEntitlementAdjustment && grantingGroups.length === 0
      ? adjustmentScopeNotice(outcomeOptions, selectedCount)
      : null;

  const flaggableEntitlements = useMemo(
    () =>
      entitlements.filter(
        (
          entitlement,
        ): entitlement is MFAccessReviewAccountEntitlement & {
          snapshotEntitlementId: string;
        } => Boolean(entitlement.snapshotEntitlementId),
      ),
    [entitlements],
  );

  // Show entitlements section if this is an adjustment outcome and we have
  // entitlements — never in an entitlement-only cycle, where the row IS the
  // entitlement and the picker would ask the reviewer to re-select it.
  const showEntitlementsFlagging =
    offersEntitlementFlagging(
      triggersEntitlementAdjustment,
      flaggableEntitlements.length,
      entitlementOnly,
    ) && !!setFlaggedSnapshotIds;

  // An adjustment that flags every entitlement is a full removal in all but
  // name, and the server records it as the removal outcome (PLO-5461). Every
  // warning and gate below keys off what is actually submitted, not off the
  // button the reviewer pressed — otherwise this path starts an irreversible
  // de-provision without the acknowledgement the removal outcome demands.
  // Accounts on one resource share entitlement ids, so coverage is counted per
  // account rather than against the merged list on screen: a selection that
  // looks partial here can still be everything one of the accounts holds.
  // Never converts in an entitlement-only cycle: a scoped row's removal is
  // already limited to its one entitlement, so re-recording it as a full
  // account removal — and demanding that outcome's consent gate — would claim
  // more than the server does (PLO-5731).
  const convertingCount = adjustmentConversionCount(
    accountEntitlementIds,
    flaggedSnapshotIds,
    entitlementOnly,
  );
  const emptiesAnAccount = convertingCount > 0;

  const submittedOutcome = submittedOutcomeFor(
    reviewAction,
    triggersEntitlementAdjustment,
    emptiesAnAccount,
    outcomeOptions,
  );

  const convertedToRemoval = submittedOutcome !== reviewAction;

  // Whether confirming removes the access THERE AND THEN — read from the
  // cycle's configured actions, never from the outcome's name. A cycle with
  // auto-deprovision off and deprovision-on-approval on removes nothing when
  // the reviewer decides, so it gets no warning, no acknowledgement toggle and
  // no owner certification (PLO-5804). Decision-only cycles never deprovision
  // at all (PLO-5083).
  const removal = decisionRemovalTiming(
    submittedOutcome,
    outcomeOptions,
    decisionOnly,
  );
  const removesAccessNow = decisionRemovesAccessNow(
    submittedOutcome,
    outcomeOptions,
    decisionOnly,
  );

  const cancelsRemovalNotice = pendingRemovalNotice({
    pendingCount: pendingRemovalCount,
    selectedCount,
    noun,
    cancels: decisionCancelsPendingRemoval(submittedOutcome, outcomeOptions, {
      markAsRemoved,
      decisionOnly,
    }),
  });

  const coverageNotice = fullCoverageNotice(
    outcomeOptions,
    convertingCount,
    selectedCount,
    removal,
  );

  // Icon, tone and consequence sentence follow the submitted outcome too: a
  // fully flagged adjustment is confirming a removal, and must read like one.
  const meta = outcomeMeta(submittedOutcome, removal);
  const iconTint = TONE_ICON_TINT[meta.tone];

  // In an entitlement-only cycle a removal takes away the row's ONE entitlement
  // and leaves the rest of the account alone, so the consequence sentence names
  // it. Timing still comes from the cycle's configured actions, never from the
  // outcome's name (PLO-5804).
  const consequenceLine =
    entitlementOnly && isLockingOutcome(submittedOutcome)
      ? entitlementRemovalLine({
          entitlementLabel:
            entitlements.length === 1
              ? entitlementChipLabel(entitlements[0])
              : null,
          personName: selectedAccountName,
          selectedCount,
          removal,
        })
      : meta.line;

  // Helper to toggle entitlement flagging
  const handleToggleEntitlement = (id: string) => {
    if (!setFlaggedSnapshotIds) return;
    if (flaggedSnapshotIds.includes(id)) {
      setFlaggedSnapshotIds(flaggedSnapshotIds.filter((i) => i !== id));
    } else {
      setFlaggedSnapshotIds([...flaggedSnapshotIds, id]);
    }
  };

  // On group-held access the reviewer ticks units — a group's entitlements
  // together, or one ungrouped entitlement — and the flags follow from them.
  const pickByGroup = grantingGroups.length > 0;
  const units = useMemo(
    () =>
      pickByGroup ? pickerUnits(grantingGroups, flaggableEntitlements) : [],
    [pickByGroup, grantingGroups, flaggableEntitlements],
  );
  const [tickedUnits, setTickedUnits] = useState<ReadonlySet<string>>(
    new Set(),
  );
  useEffect(() => {
    setTickedUnits(new Set());
  }, [isOpen]);
  const tickUnits = (ids: string[]) => {
    if (!setFlaggedSnapshotIds) return;
    const next = new Set(ids);
    setTickedUnits(next);
    setFlaggedSnapshotIds(
      flagsForGroups(
        next,
        units,
        flaggableEntitlements.map((e) => e.snapshotEntitlementId),
      ),
    );
  };
  const handleToggleUnit = (id: string) =>
    tickUnits(toggleFilterValue([...tickedUnits], id));

  // Select all / deselect all entitlements
  const handleSelectAllEntitlements = (checked: boolean) => {
    if (!setFlaggedSnapshotIds) return;
    if (pickByGroup) {
      tickUnits(checked ? units.map((u) => u.id) : []);
      return;
    }
    if (checked) {
      setFlaggedSnapshotIds(
        flaggableEntitlements.map((e) => e.snapshotEntitlementId),
      );
    } else {
      setFlaggedSnapshotIds([]);
    }
  };

  // Explicit consent to immediate, irreversible deprovisioning. Demanded only
  // when this decision actually starts one, and only when the actor is NOT the
  // resource owner (owners certify via their own checkbox below). Resets
  // whenever the modal reopens or the chosen action changes.
  const needsDeprovisionConsent = removesAccessNow && !isResourceOwner;
  const [deprovisionConsent, setDeprovisionConsent] = useState(false);
  // Flagging the last entitlement changes the consequence, so a consent given
  // for an adjustment must not carry over into the removal it just became.
  useEffect(() => {
    setDeprovisionConsent(false);
  }, [isOpen, reviewAction, emptiesAnAccount]);

  // Search state for entitlements
  const [entitlementSearch, setEntitlementSearch] = useState("");

  // Filtered entitlements based on search
  const filteredEntitlements = useMemo(() => {
    if (!entitlementSearch.trim()) return flaggableEntitlements;
    const query = entitlementSearch.toLowerCase();
    return flaggableEntitlements.filter(
      (e) =>
        e.snapshotEntitlementName.toLowerCase().includes(query) ||
        (e.snapshotEntitlementType?.toLowerCase().includes(query) ?? false),
    );
  }, [flaggableEntitlements, entitlementSearch]);

  const filteredUnits = useMemo(() => {
    if (!entitlementSearch.trim()) return units;
    const query = entitlementSearch.toLowerCase();
    return units.filter((u) =>
      [u.name, ...u.entitlementNames].some((n) =>
        n.toLowerCase().includes(query),
      ),
    );
  }, [units, entitlementSearch]);

  const flaggedSet = useMemo(
    () => new Set(flaggedSnapshotIds),
    [flaggedSnapshotIds],
  );
  // Select-all reads the reviewer's ticks: on group-held access that is the
  // units, since a unit can share or lack flaggable ids (PLO-6260).
  const [pickedCount, pickableCount] = pickByGroup
    ? [units.filter((u) => tickedUnits.has(u.id)).length, units.length]
    : [flaggedSnapshotIds.length, flaggableEntitlements.length];
  const allPicked = pickableCount > 0 && pickedCount === pickableCount;
  const pickerEmpty = pickByGroup
    ? filteredUnits.length === 0
    : filteredEntitlements.length === 0;

  const confirmDisabled =
    (reviewAction === "out_of_scope" && !outOfScopeReason) ||
    (requiresNote && reviewAction !== "out_of_scope" && !note) ||
    (!!showEntitlementsFlagging && flaggedSnapshotIds.length === 0) ||
    // One flag set cannot speak for accounts that hold the access differently:
    // it would flag ids on accounts outside the group picked (PLO-6260).
    (!!showEntitlementsFlagging && adjustSeparately) ||
    (needsDeprovisionConsent && !deprovisionConsent);

  const confirmLabel =
    removesAccessNow && isResourceOwner && markAsRemoved
      ? "Confirm and certify removal"
      : "Confirm";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="lg"
      initialFocusRef={noteFieldRef}
    >
      <ModalOverlay bg="rgba(0, 0, 0, 0.3)" backdropFilter="blur(2px)" />
      <ModalContent
        borderRadius="component.4xl"
        boxShadow="modal"
        overflow="hidden"
      >
        <ModalHeader px="1xl" pt="1xl" pb={0}>
          <Flex align="flex-start" justify="space-between" gap="md">
            <HStack spacing="md" align="center" minW={0}>
              <Flex
                w="24px"
                h="24px"
                flexShrink={0}
                align="center"
                justify="center"
                borderRadius="full"
                bg={iconTint.bg}
              >
                <Icon as={meta.icon} boxSize="14px" color={iconTint.color} />
              </Flex>
              <Text
                fontFamily="heading"
                fontWeight={500}
                fontSize="lg"
                color="text.body.primary"
                lineHeight="1.2"
              >
                {title}
              </Text>
            </HStack>
          </Flex>
        </ModalHeader>
        <ModalCloseButton
          top="1xl"
          right="1xl"
          borderRadius="component.md"
          _focus={{ boxShadow: "none" }}
          _focusVisible={{ boxShadow: "focusRing" }}
        />

        <ModalBody px="1xl" py="lg">
          <VStack spacing="lg" align="stretch">
            <Text fontSize="smd" color="text.body.secondary" lineHeight="1.5">
              You&rsquo;re recording a decision for{" "}
              <Text as="span" fontWeight={600} color="text.body.primary">
                {subject}
              </Text>
              . {consequenceLine}
            </Text>

            {convertedToRemoval && (
              <HStack
                align="flex-start"
                spacing="md"
                bg="error.50"
                border="1px solid"
                borderColor="error.300"
                borderRadius="component.2xl"
                p="lg"
              >
                <Icon
                  as={AlertTriangle}
                  boxSize="20px"
                  flexShrink={0}
                  color="error.600"
                  mt="1px"
                />
                <VStack align="flex-start" spacing="2xs">
                  <Text
                    fontSize="smd"
                    fontWeight={600}
                    color="text.body.primary"
                  >
                    {coverageNotice.title}
                  </Text>
                  <Text
                    fontSize="smd"
                    color="text.body.secondary"
                    lineHeight="1.5"
                  >
                    {coverageNotice.body}
                  </Text>
                </VStack>
              </HStack>
            )}

            {removesAccessNow && (
              <HStack
                align="flex-start"
                spacing="md"
                bg="warning.50"
                border="1px solid"
                borderColor="warning.300"
                borderRadius="component.2xl"
                p="lg"
              >
                <Flex
                  w="20px"
                  h="20px"
                  flexShrink={0}
                  align="center"
                  justify="center"
                  borderRadius="full"
                  bg="warning.500"
                  mt="1px"
                >
                  <Text
                    fontSize="xs"
                    fontWeight={700}
                    color="white"
                    lineHeight="1"
                  >
                    !
                  </Text>
                </Flex>
                <VStack align="flex-start" spacing="2xs">
                  <Text
                    fontSize="smd"
                    fontWeight={600}
                    color="text.body.primary"
                  >
                    De-provisioning starts immediately
                  </Text>
                  <Text
                    fontSize="smd"
                    color="text.body.secondary"
                    lineHeight="1.5"
                  >
                    Confirming automatically begins de-provisioning to remove{" "}
                    {entitlementOnly
                      ? isBulk
                        ? `the selected ${noun}s`
                        : `this ${noun}`
                      : "this access"}
                    . It won&rsquo;t wait for the review to be submitted.
                  </Text>
                </VStack>
              </HStack>
            )}

            {cancelsRemovalNotice && (
              <HStack
                align="flex-start"
                spacing="md"
                bg="warning.50"
                border="1px solid"
                borderColor="warning.300"
                borderRadius="component.2xl"
                p="lg"
              >
                <Icon
                  as={AlertTriangle}
                  boxSize="20px"
                  flexShrink={0}
                  color="warning.600"
                  mt="1px"
                />
                <VStack align="flex-start" spacing="2xs">
                  <Text
                    fontSize="smd"
                    fontWeight={600}
                    color="text.body.primary"
                  >
                    {cancelsRemovalNotice.title}
                  </Text>
                  <Text
                    fontSize="smd"
                    color="text.body.secondary"
                    lineHeight="1.5"
                  >
                    {cancelsRemovalNotice.body}
                  </Text>
                </VStack>
              </HStack>
            )}

            {scopeNotice && (
              <HStack
                align="flex-start"
                spacing="md"
                bg="bg.section"
                border="1px solid"
                borderColor="border.default"
                borderRadius="component.2xl"
                p="lg"
              >
                <Icon
                  as={Info}
                  boxSize="20px"
                  flexShrink={0}
                  color="text.body.secondary"
                  mt="1px"
                />
                <VStack align="flex-start" spacing="2xs">
                  <Text
                    fontSize="smd"
                    fontWeight={600}
                    color="text.body.primary"
                  >
                    {scopeNotice.title}
                  </Text>
                  <Text
                    fontSize="smd"
                    color="text.body.secondary"
                    lineHeight="1.5"
                  >
                    {scopeNotice.body}
                  </Text>
                </VStack>
              </HStack>
            )}

            {reviewAction === "out_of_scope" && (
              <NoteField
                label="Add a reason"
                required
                value={outOfScopeReason}
                onChange={setOutOfScopeReason}
                placeholder="Why is this access out of scope for the review?"
              />
            )}

            <NoteField
              label={
                requiresNote && reviewAction !== "out_of_scope"
                  ? "Add a note"
                  : "Add a note (optional)"
              }
              required={requiresNote && reviewAction !== "out_of_scope"}
              value={note}
              onChange={setNote}
              placeholder="Context for the approver…"
              inputRef={noteFieldRef}
            />

            {/* Acknowledge de-provisioning — Confirm is gated on this toggle. */}
            {needsDeprovisionConsent && (
              <HStack spacing="md" align="center">
                <DsSwitch
                  checked={deprovisionConsent}
                  onChange={setDeprovisionConsent}
                  aria-label="Acknowledge de-provisioning starts immediately"
                />
                <Text
                  fontSize="smd"
                  color="text.body.primary"
                  cursor="pointer"
                  onClick={() => setDeprovisionConsent((v) => !v)}
                >
                  I acknowledge this will start de-provisioning immediately
                </Text>
              </HStack>
            )}

            {/* Entitlement flagging section */}
            {showEntitlementsFlagging && (
              <>
                <Divider />
                <FormControl>
                  <FormLabel
                    fontSize="smd"
                    fontWeight={500}
                    color="text.body.secondary"
                  >
                    <HStack spacing="sm">
                      <Icon as={Flag} boxSize={4} color="warning.500" />
                      <Text>
                        {pickByGroup
                          ? "Select entitlements to remove"
                          : "Flag entitlements for adjustment"}
                      </Text>
                    </HStack>
                  </FormLabel>
                  {!pickByGroup && (
                    <Text fontSize="xs" color="text.body.tertiary" mb="md">
                      Select which entitlements need to be adjusted. Only
                      flagged entitlements will be included in the remediation
                      task.
                    </Text>
                  )}
                  {adjustSeparately && (
                    <Text fontSize="xs" color="warning.600" mb="md">
                      These accounts hold this access differently. Adjust them
                      separately.
                    </Text>
                  )}
                  {pickByGroup &&
                    tickedUnits.size > 0 &&
                    flaggedSnapshotIds.length === 0 && (
                      <Text fontSize="xs" color="warning.600" mb="md">
                        This access is also granted another way, so removing
                        only these changes nothing.
                      </Text>
                    )}

                  {/* Search input for entitlements */}
                  {flaggableEntitlements.length > 5 && (
                    <InputGroup size="sm" mb="sm">
                      <InputLeftElement>
                        <Icon
                          as={Search}
                          boxSize={4}
                          color="text.body.tertiary"
                        />
                      </InputLeftElement>
                      <Input
                        placeholder="Search entitlements…"
                        value={entitlementSearch}
                        onChange={(e) => setEntitlementSearch(e.target.value)}
                        borderRadius="component.md"
                        borderColor="border.default"
                      />
                    </InputGroup>
                  )}

                  {/* Entitlements list, with select all as its header row */}
                  <Box
                    borderWidth="1px"
                    borderColor="border.default"
                    borderRadius="component.lg"
                    overflow="hidden"
                  >
                    <HStack
                      px="md"
                      py="sm"
                      spacing="md"
                      bg="bg.elevated"
                      borderBottomWidth="1px"
                      borderColor="border.default"
                    >
                      <Checkbox
                        isChecked={allPicked}
                        isIndeterminate={pickedCount > 0 && !allPicked}
                        onChange={(e) =>
                          handleSelectAllEntitlements(e.target.checked)
                        }
                      >
                        <Text
                          fontSize="xs"
                          fontWeight={500}
                          color="text.body.secondary"
                          ml="2xs"
                        >
                          {allPicked ? "Deselect all" : "Select all"}
                        </Text>
                      </Checkbox>
                      {flaggedSnapshotIds.length > 0 && (
                        <Text
                          ml="auto"
                          fontSize="xs"
                          color="text.body.tertiary"
                        >
                          {flaggedSnapshotIds.length} selected
                        </Text>
                      )}
                    </HStack>
                    <Box
                      maxHeight={pickByGroup ? "320px" : "200px"}
                      overflowY="auto"
                    >
                      <VStack
                        align="stretch"
                        spacing={0}
                        divider={<StackDivider borderColor="border.subtle" />}
                      >
                        {pickerEmpty ? (
                          <Text
                            fontSize="smd"
                            color="text.body.tertiary"
                            textAlign="center"
                            py="md"
                          >
                            No entitlements found
                          </Text>
                        ) : pickByGroup ? (
                          filteredUnits.map((unit) =>
                            unit.together ? (
                              <TogetherBlock
                                key={unit.id}
                                checked={tickedUnits.has(unit.id)}
                                onToggle={() => handleToggleUnit(unit.id)}
                                groupName={unit.name}
                                entitlementNames={unit.entitlementNames}
                              />
                            ) : (
                              <PickerRow
                                key={unit.id}
                                checked={tickedUnits.has(unit.id)}
                                onToggle={() => handleToggleUnit(unit.id)}
                                title={unit.name}
                              />
                            ),
                          )
                        ) : (
                          filteredEntitlements.map((ent) => (
                            <PickerRow
                              key={ent.snapshotEntitlementId}
                              checked={flaggedSet.has(
                                ent.snapshotEntitlementId,
                              )}
                              onToggle={() =>
                                handleToggleEntitlement(
                                  ent.snapshotEntitlementId,
                                )
                              }
                              title={ent.snapshotEntitlementName}
                              subtitle={ent.snapshotEntitlementType}
                            />
                          ))
                        )}
                      </VStack>
                    </Box>
                  </Box>
                </FormControl>
              </>
            )}

            {/* Resource owner certification for negative outcomes */}
            {isResourceOwner && removesAccessNow && setMarkAsRemoved && (
              <HStack spacing="md" align="flex-start">
                <Box pt="2px">
                  <DsSwitch
                    checked={!!markAsRemoved}
                    onChange={setMarkAsRemoved}
                    aria-label="Certify this access has already been removed"
                  />
                </Box>
                <Text
                  fontSize="smd"
                  color="text.body.primary"
                  cursor="pointer"
                  onClick={() => setMarkAsRemoved(!markAsRemoved)}
                >
                  As the resource owner I have already removed{" "}
                  {isBulk ? `these ${noun}s` : `this ${noun}`} and I certify{" "}
                  {isBulk ? "they have" : "it has"} been deprovisioned
                  (selecting this means the deprovisioning process does not
                  run).
                </Text>
              </HStack>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter px="1xl" pt={0} pb="1xl">
          <Button
            variant="subtle"
            tone="neutral"
            onClick={onClose}
            size="md"
            style={{ marginRight: "12px" }}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            tone={meta.tone}
            isLoading={isSubmitting}
            onClick={onSubmit}
            isDisabled={confirmDisabled}
            size="md"
            leadingIcon={<Icon as={meta.icon} boxSize="16px" />}
          >
            {confirmLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ReviewModal;
